import { CrudService } from "@/services/crudService";
import { config } from "@/services/configService";
import {
  LoadPricePayload,
  PrebookPayload,
  PublicBookingsListPayload,
} from "./types";

const baseUrl = "/publicbookings";
const crud = new CrudService({
  baseUrl: config.apiUrl,
  handleUnauthorized: false,
});

type CancelBookingOptions = {
  tenantUrl?: string | null;
};

type LoadPriceOptions = {
  tenantUrl?: string | null;
};

function normalizeTenantUrl(url?: string | null): string | null {
  if (typeof url !== "string") {
    return null;
  }

  const trimmed = url.trim();
  if (!trimmed) {
    return null;
  }

  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed.replace(/\/+$/, "");
  }

  if (trimmed.startsWith("//")) {
    return `https:${trimmed}`.replace(/\/+$/, "");
  }

  return `https://${trimmed.replace(/^\/+/, "")}`.replace(/\/+$/, "");
}

function buildTenantEndpoint(base: string, path: string): string {
  const normalizedBase = normalizeTenantUrl(base);
  if (!normalizedBase) {
    throw new Error("Tenant URL is required for Marketplace cancellations");
  }

  const trimmedPath = path.startsWith("/") ? path : `/${path}`;
  return `${normalizedBase}${trimmedPath}`;
}

export function prebook(payload: PrebookPayload) {
  return crud.httpPost(`${baseUrl}/prebook`, payload, undefined, "json", {
    headers: {
      Accept: "*/*",
    },
  });
}

export function loadPrice(
  payload: LoadPricePayload,
  options: LoadPriceOptions = {},
) {
  const headers: Record<string, string> = {
    Accept: "*/*",
  };
  const hasTenantUrl =
    typeof options.tenantUrl === "string" && options.tenantUrl.trim() !== "";
  if (hasTenantUrl) {
    const tenantEndpoint = buildTenantEndpoint(
      typeof options.tenantUrl === "string" ? options.tenantUrl : "",
      "/api/publicbookings/loadprice",
    );
    const tenantCrud = new CrudService({
      baseUrl: "",
      allowAuth: true,
      handleUnauthorized: false,
    });
    return tenantCrud.httpPost(tenantEndpoint, payload, undefined, "json", {
      headers,
      handleUnauthorized: false,
    });
  }

  return crud.httpPost(`${baseUrl}/loadprice`, payload, undefined, "json", {
    headers,
    handleUnauthorized: false,
  });
}

type ListBookingsOptions = {
  headers?: Record<string, string>;
  baseUrl?: string;
};

export function listBookings(
  payload: PublicBookingsListPayload,
  options: ListBookingsOptions = {},
) {
  const headers = {
    Accept: "*/*",
    ...(options.headers ? options.headers : {}),
  };

  return crud.httpPost(`${baseUrl}/list`, payload, undefined, "json", {
    headers,
    baseUrl: options.baseUrl,
    handleUnauthorized: false,
  });
}

export function cancelBooking(
  bookingId: number,
  customerId: number,
  options: CancelBookingOptions = {},
) {
  const hasTenantUrl =
    typeof options.tenantUrl === "string" && options.tenantUrl.trim() !== "";

  const headers: Record<string, string> = {
    Accept: "*/*",
  };

  if (hasTenantUrl) {
    const tenantEndpoint = buildTenantEndpoint(
      typeof options.tenantUrl === "string" ? options.tenantUrl : "",
      `/api/publicbookings/cancel/${bookingId}/${customerId || 0}`,
    );
    const tenantCrud = new CrudService({
      baseUrl: "",
      allowAuth: true,
      handleUnauthorized: false,
    });
    return tenantCrud.httpPost(tenantEndpoint, null, undefined, "json", {
      headers: {
        ...headers,
      },
    });
  }

  return crud.httpPost(
    `${baseUrl}/cancel/${bookingId}/${customerId}`,
    null,
    undefined,
    "json",
    {
      headers: {
        ...headers,
      },
      handleUnauthorized: false,
    },
  );
}
